import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  MessageCircle,
  MoreHorizontal,
  Palette,
  Pin,
  Plus,
  Search,
  Sparkles,
  User as UserIcon,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '@/app/providers';
import { useAuthModal } from '@/features/auth/authModalStore';
import { toast } from '@/components/feedback/toast';
import { supabase } from '@/lib/supabase';
import { Button, Modal, Spinner } from '@/components/ui';
import { ROUTES } from '@/config/constants';
import { AvatarMenu } from '@/features/profile/components/AvatarMenu';
import { UserProfileModal } from '@/features/profile/components/UserProfileModal';
import { CreateCommunityModal } from '@/features/communities/components/CreateCommunityModal';
import { useCommunity } from '../hooks/useCommunities';
import {
  useDeleteMessage,
  useEditMessage,
  useInvalidateMembership,
  useMembers,
  useMembership,
  useMessages,
  useReactions,
  useSendMessage,
  useToggleReaction,
} from '../hooks/useCommunityView';
import { useCommunityRealtime } from '../hooks/useCommunityRealtime';
import * as api from '../api/communityView';
import type { MemberRole } from '../api/communityView';
import { getChannels, resolveChannel, type Channel } from './channels';
import { useChannels, useReplaceChannels } from '../hooks/useChannels';
import { CommunityThemeBackdrop, CommunityThemeScope } from '@/components/theme/CommunityThemeScope';
import { cn } from '@/lib/utils';
import { ChannelRail } from './ChannelRail';
import { ChatPane } from './ChatPane';
import { VoiceStage } from './VoiceStage';
import { CommunityPanels } from './CommunityPanels';
import { ReportModal } from './ReportModal';
import { ChannelEditorModal } from './ChannelEditorModal';
import { AdminSettingsDrawer, type AdminTab } from './AdminSettingsDrawer';
import { displayName, priceLabel } from './cvHelpers';

type Scope = 'chat' | 'pinned' | 'members' | 'about';

// The cv2 "Discord shell" surface — full-screen frosted blur (covers the whole
// viewport, including behind the app TopNav, so there's no raw-theme strip at the
// top). `pt-16` offsets the inner content below the TopNav, which floats on top
// transparently so its logo / avatar / toolkit menu stay visible and crisp.
const SHELL =
  'fixed inset-0 flex animate-cv2-fade-in flex-col overflow-hidden bg-[linear-gradient(180deg,rgba(10,10,14,0.18),rgba(10,10,14,0.3))] backdrop-blur-[22px] backdrop-saturate-[1.3]';
const ICON_BTN =
  'grid h-[38px] w-[38px] place-items-center rounded-[10px] border border-transparent bg-transparent text-white/[0.72] transition-all duration-150 hover:border-white/[0.05] hover:bg-white/[0.06] hover:text-[#fafafa]';
const MENU_ITEM_BASE =
  'flex h-[34px] w-full cursor-pointer items-center gap-2.5 rounded-sm border-0 bg-transparent px-2.5 text-left text-[13px] font-medium';
const MENU_ITEM = cn(MENU_ITEM_BASE, 'text-white/[0.72] hover:bg-white/[0.06] hover:text-[#fafafa]');
const MENU_ITEM_DANGER = cn(MENU_ITEM_BASE, 'text-[#ff7a7a] hover:bg-[rgba(239,68,68,0.1)] hover:text-[#ff8e8e]');
const CHANNEL_HEADER = 'flex h-14 flex-none items-center gap-3.5 border-b border-white/[0.05] px-[18px]';
type ActionTipSource = 'invite' | 'notifications' | 'more' | 'toolkit';
type ActionTip = { source: ActionTipSource; message: string } | null;

export function CommunityView({ communityId }: { communityId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading: authLoading, signOut } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);

  const { data: c, isLoading } = useCommunity(communityId);
  const ownerId = c?.owner_id ?? null;
  const isOwner = Boolean(user) && Boolean(ownerId) && user?.id === ownerId;

  const { data: members = [] } = useMembers(communityId, ownerId);
  const { data: messages = [] } = useMessages(communityId);
  const { data: joined = false } = useMembership(communityId, user?.id);
  const { data: reactions = [] } = useReactions(
    communityId,
    messages.map((m) => m.id),
  );
  const sendMessage = useSendMessage(communityId);
  const editMessage = useEditMessage(communityId);
  const deleteMessage = useDeleteMessage(communityId);
  const toggleReaction = useToggleReaction(communityId);
  const invalidateMembership = useInvalidateMembership(communityId);

  const selfName = user
    ? ((user.user_metadata?.full_name as string) ||
      (user.user_metadata?.name as string) ||
      user.email ||
      'Someone')
    : null;
  const { typing, sendTyping } = useCommunityRealtime(communityId, selfName);

  const [scope, setScope] = useState<Scope>('chat');
  const [activeChannelId, setActiveChannelId] = useState(
    () => (location.state as { voiceChannelId?: string } | null)?.voiceChannelId ?? 'general',
  );
  // Channels are server rows now (community_channels) — seeded lazily, owner's
  // legacy localStorage copy migrated on first load by the hook.
  const { data: serverChannels } = useChannels(communityId, ownerId);
  const replaceChannels = useReplaceChannels(communityId);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [channelEditorOpen, setChannelEditorOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminTab, setAdminTab] = useState<AdminTab>('general');
  const [search, setSearch] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [toolkitOpen, setToolkitOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [paidGateOpen, setPaidGateOpen] = useState(false);
  const [actionTip, setActionTip] = useState<ActionTip>(null);
  const [reportOpen, setReportOpen] = useState<{ kind: string; id: string } | null>(null);
  const [profilePopover, setProfilePopover] = useState<{ userId: string; anchor?: DOMRect } | null>(null);

  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
  const avatarSrc = profile?.avatar_url ?? str(meta.avatar_url) ?? str(meta.picture);
  const accountName =
    profile?.display_name ?? str(meta.full_name) ?? str(meta.name) ?? user?.email ?? null;

  // "Return to call" from the floating voice dock lands here with the voice
  // channel in location state — jump to it even if we're already mounted.
  useEffect(() => {
    const vc = (location.state as { voiceChannelId?: string } | null)?.voiceChannelId;
    if (vc) {
      setActiveChannelId(vc);
      setScope('chat');
    }
  }, [location.state]);

  const roleOf = useMemo(() => {
    const map = new Map(members.map((m) => [m.user_id, m.role] as const));
    return (id: string): MemberRole => map.get(id) ?? 'MEMBER';
  }, [members]);
  const currentUserRole = user ? roleOf(user.id) : 'MEMBER';
  const isDesignatedWinrAdmin =
    communityId === 'winr-official' &&
    Boolean(user) &&
    (user?.email?.toLowerCase() === 'tonyvivixofficial@icloud.com' ||
      profile?.email?.toLowerCase() === 'tonyvivixofficial@icloud.com' ||
      profile?.handle?.toLowerCase() === 'neotpf' ||
      profile?.display_name?.toLowerCase() === 'neotpf');
  const canManageCommunity =
    Boolean(user) && (isOwner || isDesignatedWinrAdmin || currentUserRole === 'OWNER' || currentUserRole === 'ADMIN');

  useEffect(() => {
    // If they hit the community view, are not joined, and it's paid, immediately show the gate.
    if (!isLoading && c && c.price_cents > 0 && !joined && !canManageCommunity) {
      setPaidGateOpen(true);
    }
  }, [isLoading, c, joined, canManageCommunity]);

  if (isLoading) {
    return (
      <div className={SHELL}>
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </div>
    );
  }
  if (!c) {
    return (
      <div className={SHELL}>
        <div className="relative flex h-16 flex-none items-center gap-3 px-5">
          <button
            className="grid h-9 w-9 place-items-center rounded-lg border border-white/[0.08] bg-white/[0.055] text-white/75 transition-all hover:border-accent/35 hover:bg-white/[0.09] hover:text-white"
            onClick={() => navigate(ROUTES.home)}
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
        </div>
        <div className="flex flex-1 items-center justify-center font-semibold text-white/50">
          Community not found.
        </div>
      </div>
    );
  }

  const community = c;
  const channels = serverChannels ?? getChannels(community);
  const activeChannel = resolveChannel(channels, activeChannelId);
  const online = members.filter((m) => m.online).length;

  const channelOf = (m: api.ViewMessage) => m.channel_id || 'general';
  const pinnedMessages = messages.filter((m) => channelOf(m) === 'announcements' || channelOf(m) === 'pinned');
  const channelMessages = messages.filter((m) => {
    if (channelOf(m) !== activeChannel.id) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (m.body ?? '').toLowerCase().includes(q) || displayName(m.author).toLowerCase().includes(q);
  });

  function selectChannel(id: string) {
    setActiveChannelId(id);
    setScope('chat');
  }

  function persistChannels(next: Channel[]) {
    replaceChannels.mutate(next, {
      onError: (e) => toast((e as Error).message || 'Could not save channels', 'error'),
    });
  }

  function showActionTip(source: ActionTipSource, message: string) {
    setActionTip({ source, message });
    window.setTimeout(() => {
      setActionTip((current) => (current?.source === source && current.message === message ? null : current));
    }, 2600);
  }

  function openChannelEditor(channel?: Channel) {
    if (!canManageCommunity) {
      showActionTip('more', 'Only admins can manage channels.');
      return;
    }
    setEditingChannel(channel ?? null);
    setChannelEditorOpen(true);
  }

  function saveChannel(nextChannel: Channel, previousId?: string) {
    if (!canManageCommunity) {
      showActionTip('more', 'Only admins can manage channels.');
      return;
    }
    const exists = previousId != null;
    const next = exists
      ? channels.map((ch) => (ch.id === previousId ? nextChannel : ch))
      : [...channels, nextChannel];
    persistChannels(next);
    setActiveChannelId(nextChannel.id);
    setScope('chat');
    setChannelEditorOpen(false);
    showActionTip('more', exists ? `#${nextChannel.name} updated.` : `#${nextChannel.name} created.`);
  }

  function deleteChannel(id: string) {
    if (!canManageCommunity) {
      showActionTip('more', 'Only admins can manage channels.');
      return;
    }
    if (!window.confirm(`Delete channel #${channels.find((ch) => ch.id === id)?.name ?? id}? Messages will be archived.`)) {
      return;
    }
    const next = channels.filter((ch) => ch.id !== id);
    persistChannels(next);
    if (activeChannelId === id) setActiveChannelId(resolveChannel(next, 'general').id);
    setChannelEditorOpen(false);
    showActionTip('more', 'Channel deleted.');
  }

  function openAdmin(tab: AdminTab = 'general') {
    if (!canManageCommunity) {
      showActionTip('more', 'Only admins can open community settings.');
      return;
    }
    setMoreOpen(false);
    setAdminTab(tab);
    setAdminOpen(true);
  }

  function back() {
    // Always land on "Your Communities" — `navigate(-1)` could bounce back into
    // WINR Studio (or other transient entries) when that's where the user came from.
    navigate(ROUTES.myCommunities);
  }

  function invite(source: ActionTipSource = 'invite') {
    void navigator.clipboard?.writeText(window.location.href);
    showActionTip(source, 'Invite link copied.');
  }

  async function toggleJoin() {
    if (!user) return openAuth();
    if (community.price_cents > 0 && !joined) {
      setPaidGateOpen(true);
      return;
    }
    try {
      if (joined) {
        await api.leaveCommunity(communityId, user.id);
        showActionTip('more', 'Left community.');
      } else if (communityId === 'winr-official') {
        const { error } = await supabase.rpc('auto_join_winr_official');
        if (error) throw error;
        showActionTip('more', 'Joined WINR Official.');
      } else {
        await api.joinCommunity(communityId, user.id);
        showActionTip('more', 'Joined community.');
      }
      invalidateMembership(user.id);
    } catch (e) {
      toast((e as Error).message || 'Could not update membership', 'error');
    }
  }

  function send(body: string, opts?: { media?: string | null; replyToId?: string | null }) {
    if (!user) return openAuth();
    if (!body.trim() && !opts?.media) return;
    sendMessage.mutate({
      communityId,
      channelId: activeChannel.id,
      authorId: user.id,
      body,
      media: opts?.media ?? null,
      replyToId: opts?.replyToId ?? null,
    });
  }

  function handleToggleReaction(messageId: string, emoji: string) {
    if (!user) return openAuth();
    toggleReaction.mutate({ messageId, userId: user.id, emoji });
  }

  async function submitReport(reason: string, note: string) {
    const target = reportOpen;
    setReportOpen(null);
    if (!user || !target) {
      if (!user) openAuth();
      return;
    }
    try {
      await api.submitReport({
        communityId,
        targetType: target.kind,
        targetId: target.id,
        reason,
        note,
        reporterId: user.id,
      });
      showActionTip('more', 'Report submitted. Moderators will review.');
    } catch (e) {
      toast((e as Error).message || 'Could not submit report', 'error');
    }
  }

  const openProfile = (id: string, anchor?: DOMRect) => {
    if (id) setProfilePopover({ userId: id, anchor });
  };

  const TABS: { scope: Scope; label: string; Icon: typeof MessageCircle; count?: number }[] = [
    { scope: 'chat', label: 'Chat', Icon: MessageCircle },
    { scope: 'pinned', label: 'Pinned', Icon: Pin, count: pinnedMessages.length },
    { scope: 'members', label: 'Members', Icon: Users, count: members.length },
  ];

  return (
    <CommunityThemeScope doc={community.theme_doc}>
    <div className={SHELL}>
      <CommunityThemeBackdrop />
      <div className="relative z-[1] flex h-16 flex-none items-center gap-3 px-5">
        {/* Back lives on the community banner in the rail (overlaid on the image). */}
        <div className="flex flex-none items-center gap-2" />

        <div className="pointer-events-none absolute bottom-0 left-1/2 top-0 z-[1] flex h-16 -translate-x-1/2 items-center [&>*]:pointer-events-auto">
          <div className="inline-flex items-center gap-1" role="tablist">
            {TABS.map((t) => {
              const active = scope === t.scope;
              return (
                <button
                  key={t.scope}
                  className={cn(
                    "relative inline-flex h-10 cursor-pointer items-center gap-[9px] whitespace-nowrap rounded-[10px] border-0 bg-transparent px-[18px] text-sm font-semibold tracking-[-0.1px] transition-[color,background] duration-[180ms] [&_svg]:opacity-60",
                    active
                      ? "text-[#fafafa] [&_svg]:opacity-100 after:absolute after:inset-x-[18px] after:-bottom-2.5 after:h-0.5 after:rounded-[2px] after:bg-white/85 after:content-['']"
                      : 'text-white/[0.46] hover:bg-white/[0.04] hover:text-[#fafafa]',
                  )}
                  onClick={() => setScope(t.scope)}
                >
                  <t.Icon size={15} />
                  <span>{t.label}</span>
                  {t.count != null && t.count > 0 && (
                    <span
                      className={cn(
                        'inline-grid h-5 min-w-[20px] place-items-center rounded-pill px-1.5 text-[11px] font-bold',
                        active ? 'bg-white/[0.14] text-[#fafafa]' : 'bg-white/[0.07] text-white/[0.72]',
                      )}
                    >
                      {t.count > 999 ? '999+' : t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="relative z-[2] ml-auto flex flex-none items-center gap-1.5">
          <span className="relative">
            <button className={ICON_BTN} title="Invite" onClick={() => invite('invite')}>
              <UserPlus size={18} />
            </button>
            <InlineActionTip open={actionTip?.source === 'invite'}>{actionTip?.message}</InlineActionTip>
          </span>

          <div
            className={cn(
              'flex h-10 w-[260px] items-center gap-[9px] rounded-pill border bg-white/[0.04] px-4 text-white/[0.46] transition-[border-color,background,width,box-shadow] focus-within:w-80 focus-within:border-white focus-within:bg-white/[0.96] focus-within:text-[#0a0a0e] focus-within:shadow-[0_6px_22px_rgba(0,0,0,0.4),0_0_0_4px_rgba(255,255,255,0.06)]',
              search ? 'w-80 border-white bg-white/[0.96] text-[#0a0a0e] shadow-[0_6px_22px_rgba(0,0,0,0.4),0_0_0_4px_rgba(255,255,255,0.06)]' : 'border-white/[0.12]',
            )}
          >
            <Search size={14} />
            <input
              className={cn(
                'min-w-0 flex-1 border-0 bg-transparent text-[13px] font-medium outline-none focus:text-[#0a0a0e]',
                search ? 'text-[#0a0a0e] placeholder:text-black/40' : 'text-[#fafafa]',
              )}
              placeholder="Search messages"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setSearch('')}
            />
            {search && (
              <button
                className="grid h-[18px] w-[18px] flex-none cursor-pointer place-items-center rounded-full border-0 bg-black/40 text-[11px] leading-none text-white"
                onClick={() => setSearch('')}
              >
                <X size={11} />
              </button>
            )}
          </div>

          {canManageCommunity && (
            <button
              className={ICON_BTN}
              title="WINR Studio"
              aria-label="WINR Studio"
              onClick={() => navigate(ROUTES.themeStudioFor(communityId))}
            >
              <Palette size={18} />
            </button>
          )}

          <span className="relative">
            <button className={ICON_BTN} title="Notifications" onClick={() => showActionTip('notifications', 'Notification settings coming soon.')}>
              <Bell size={18} />
            </button>
            <InlineActionTip open={actionTip?.source === 'notifications'}>{actionTip?.message}</InlineActionTip>
          </span>

          <span className="relative">
            <button className={ICON_BTN} title="More" onClick={() => setMoreOpen((v) => !v)}>
              <MoreHorizontal size={18} />
            </button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-[90]" onClick={() => setMoreOpen(false)} />
                <div className="absolute right-0 top-[calc(100%+8px)] z-[200] w-[280px] rounded-md border border-white/10 bg-[rgba(16,16,22,0.98)] p-1.5 shadow-[0_24px_60px_rgba(0,0,0,0.7)] backdrop-blur-[24px]">
                  <div className="px-2.5 pb-1 pt-2 text-[10.5px] font-bold uppercase tracking-[0.7px] text-white/[0.46]">Community</div>
                  <button type="button" className={MENU_ITEM} onClick={() => { setMoreOpen(false); invite('more'); }}>
                    Copy invite link
                  </button>
                  <button type="button" className={MENU_ITEM} onClick={() => { setMoreOpen(false); showActionTip('more', 'Notification preferences coming soon.'); }}>
                    Notification preferences
                  </button>
                  {canManageCommunity && (
                    <>
                      <div className="mx-1.5 my-1 h-px bg-white/[0.05]" />
                      <div className="px-2.5 pb-1 pt-2 text-[10.5px] font-bold uppercase tracking-[0.7px] text-white/[0.46]">Admin</div>
                      <button type="button" className={MENU_ITEM} onClick={() => openAdmin('general')}>Manage community</button>
                      <button type="button" className={MENU_ITEM} onClick={() => openAdmin('channels')}>Channels</button>
                      <button type="button" className={MENU_ITEM} onClick={() => openAdmin('roles')}>Roles & permissions</button>
                      <button type="button" className={MENU_ITEM} onClick={() => openAdmin('members')}>Members</button>
                      <button type="button" className={MENU_ITEM} onClick={() => openAdmin('invites')}>Invites</button>
                      <button type="button" className={MENU_ITEM} onClick={() => openAdmin('moderation')}>Moderation log</button>
                      <button type="button" className={MENU_ITEM} onClick={() => openAdmin('analytics')}>Analytics</button>
                    </>
                  )}
                  <div className="mx-1.5 my-1 h-px bg-white/[0.05]" />
                  <button
                    type="button"
                    className={MENU_ITEM_DANGER}
                    onClick={() => { setMoreOpen(false); setReportOpen({ kind: 'community', id: communityId }); }}
                  >
                    Report community
                  </button>
                  {joined && !canManageCommunity && (
                    <button type="button" className={MENU_ITEM_DANGER} onClick={() => { setMoreOpen(false); void toggleJoin(); }}>
                      Leave community
                    </button>
                  )}
                </div>
              </>
            )}
            <InlineActionTip open={actionTip?.source === 'more'}>{actionTip?.message}</InlineActionTip>
          </span>

          <span className="relative">
            <button
              className={ICON_BTN}
              title="WINR toolkit"
              aria-expanded={toolkitOpen}
              onClick={() => setToolkitOpen((v) => !v)}
            >
              <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={cn('transition-transform duration-200', toolkitOpen && 'rotate-180')}
              >
                <path d="M6 5l6 6 6-6M6 13l6 6 6-6" />
              </svg>
            </button>
            {toolkitOpen && (
              <>
                <div className="fixed inset-0 z-[90]" onClick={() => setToolkitOpen(false)} />
                <div className="absolute right-0 top-[calc(100%+8px)] z-[200] w-72 origin-top-right animate-fade-in-up overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(165deg,rgba(44,44,58,0.1),rgba(15,15,22,0.08))] p-2 shadow-[0_24px_60px_rgba(0,0,0,0.5)] backdrop-blur-2xl">
                  <ToolkitRow
                    icon={<Plus size={15} strokeWidth={2.5} />}
                    tint="text-emerald-400 bg-emerald-500/15"
                    title="Create a community"
                    desc="Launch your own space"
                    onClick={() => {
                      setToolkitOpen(false);
                      setCreateOpen(true);
                    }}
                  />
                  <ToolkitRow
                    icon={<Sparkles size={15} strokeWidth={2.2} />}
                    tint="text-accent bg-accent/15"
                    title="WINR AI"
                    desc="Your creator copilot"
                    onClick={() => {
                      setToolkitOpen(false);
                      showActionTip('toolkit', 'WINR AI coming soon.');
                    }}
                  />
                </div>
              </>
            )}
            <InlineActionTip open={actionTip?.source === 'toolkit'}>{actionTip?.message}</InlineActionTip>
          </span>

          {!authLoading &&
            (user ? (
              <AvatarMenu
                avatarSrc={avatarSrc}
                displayName={accountName}
                email={user.email}
                onViewProfile={() => navigate(ROUTES.profileSelf)}
                onSettings={() => navigate(ROUTES.settings)}
                onLogout={() => void signOut()}
              />
            ) : (
              <button
                onClick={openAuth}
                aria-label="Sign in"
                className="ml-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white/15 text-hi transition-colors hover:border-white/35"
              >
                <UserIcon size={18} strokeWidth={2} />
              </button>
            ))}
        </div>
      </div>

      <div className="relative z-[1] grid min-h-0 flex-1 grid-cols-[260px_1fr] max-[1100px]:grid-cols-[240px_1fr]">
        <ChannelRail
          community={community}
          channels={channels}
          activeChannelId={activeChannel.id}
          inChat={scope === 'chat'}
          isOwner={canManageCommunity}
          joined={joined}
          online={online}
          members={members.length}
          onBack={back}
          onSelectChannel={selectChannel}
          onToggleJoin={() => void toggleJoin()}
          onOpenOverview={() => setScope('about')}
          onOpenChannelEditor={openChannelEditor}
        />

        <main className="flex min-h-0 flex-col">
          {scope === 'chat' && activeChannel.kind === 'voice' ? (
            <VoiceStage
              communityId={communityId}
              channel={activeChannel}
              self={user ? { id: user.id, name: accountName ?? selfName ?? 'You', avatarSrc } : null}
              onInvite={() => invite('invite')}
              onDisconnect={() => selectChannel('general')}
              onOpenChat={() => selectChannel('general')}
            />
          ) : scope === 'chat' ? (
            <ChatPane
              channel={activeChannel}
              messages={channelMessages}
              members={members}
              reactions={reactions}
              roleOf={roleOf}
              currentUserId={user?.id ?? null}
              isOwner={canManageCommunity}
              signedIn={Boolean(user)}
              joined={joined}
              sending={sendMessage.isPending}
              typing={typing}
              onSend={send}
              onUploadImage={(file) => api.uploadMessageImage(file, communityId)}
              onToggleReaction={handleToggleReaction}
              onTyping={sendTyping}
              onEdit={(id, body) => editMessage.mutate({ id, body })}
              onDelete={(id) => deleteMessage.mutate(id)}
              onRequireJoin={() => void toggleJoin()}
              onOpenProfile={openProfile}
              onReport={(id) => setReportOpen({ kind: 'post', id })}
            />
          ) : (
            <>
              <div className={CHANNEL_HEADER}>
                <span className="text-[15px] font-extrabold tracking-[-0.2px] text-[#fafafa]">
                  {scope === 'pinned' ? 'Pinned' : scope === 'members' ? 'Members' : 'Overview'}
                </span>
              </div>
              <CommunityPanels
                scope={scope}
                community={community}
                members={members}
                pinnedMessages={pinnedMessages}
                onOpenProfile={openProfile}
                onOpenChannel={selectChannel}
              />
            </>
          )}
        </main>
      </div>

      {reportOpen && (
        <ReportModal onClose={() => setReportOpen(null)} onSubmit={(r, n) => void submitReport(r, n)} />
      )}
      <ChannelEditorModal
        open={channelEditorOpen}
        channel={editingChannel}
        channels={channels}
        onClose={() => setChannelEditorOpen(false)}
        onSave={saveChannel}
        onDelete={deleteChannel}
      />
      <AdminSettingsDrawer
        open={adminOpen}
        initialTab={adminTab}
        community={community}
        channels={channels}
        members={members}
        messages={messages}
        onClose={() => setAdminOpen(false)}
        onTabChange={setAdminTab}
        onOpenChannelEditor={(channel) => {
          setAdminOpen(false);
          openChannelEditor(channel);
        }}
        onDeleteChannel={deleteChannel}
      />
      <CreateCommunityModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <UserProfileModal
        userId={profilePopover?.userId ?? null}
        anchorRect={profilePopover?.anchor}
        onClose={() => setProfilePopover(null)}
      />
      <PaidAccessModal
        open={paidGateOpen}
        communityName={community.name}
        price={priceLabel(community.price_cents)}
        onClose={() => setPaidGateOpen(false)}
      />
    </div>
    </CommunityThemeScope>
  );
}

function InlineActionTip({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute right-0 top-[calc(100%+4px)] z-[210] grid w-[220px] overflow-hidden text-right transition-[grid-template-rows] duration-300 ease-cinematic',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className="min-h-0">
        <div
          className={cn(
            'ml-auto max-w-[220px] px-1 text-[11px] font-semibold leading-[1.45] text-white/62 transition-[opacity,transform,padding] duration-300',
            open ? 'translate-y-0 pb-2 pt-1 opacity-100' : '-translate-y-1 py-0 opacity-0',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function PaidAccessModal({
  open,
  communityName,
  price,
  onClose,
}: {
  open: boolean;
  communityName: string;
  price: string;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} className="max-w-[460px] rounded-2xl border border-white/10 bg-[rgba(16,18,24,0.92)] p-0 backdrop-blur-2xl">
      <div className="overflow-hidden rounded-2xl p-6">
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-accent/15 text-accent shadow-[0_0_28px_rgba(84,185,254,0.18)]">
          <Pin size={20} strokeWidth={2.4} />
        </div>
        <h2 className="text-xl font-black tracking-[-0.4px] text-hi">Membership required</h2>
        <p className="mt-2 text-sm leading-relaxed text-mid">
          {communityName} is a paid community. You will need an active {price} membership before you can join,
          post, or access member-only channels.
        </p>
        <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.6px] text-lo">What happens next</div>
          <div className="mt-2 text-sm leading-relaxed text-white/70">
            Checkout will confirm the subscription first, then WINR will add you as a member automatically.
            You will not be joined until payment is completed.
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Not now
          </Button>
          <Button type="button" onClick={onClose} className="bg-white text-black hover:brightness-105">
            Continue to checkout
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ToolkitRow({
  icon,
  tint,
  title,
  desc,
  onClick,
}: {
  icon: ReactNode;
  tint: string;
  title: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left transition-all duration-150 hover:bg-white/[0.06] active:scale-[0.98]"
    >
      <span className={cn('grid h-8 w-8 flex-none place-items-center rounded-lg', tint)}>{icon}</span>
      <span className="min-w-0">
        <span className="block text-[13px] font-bold text-hi">{title}</span>
        <span className="block text-[11px] text-mid">{desc}</span>
      </span>
    </button>
  );
}
